<template>
  <Loading v-if="componentLoading" :style="style" />
  <template v-else>
    <Chart class="map-2d-chart" ref="chartComponent" :option="chartConfig" :style="style" />
  </template>
  <slot></slot>
</template>

<script lang="ts" setup>
import Chart from "@p-v/packages/Charts/BaseChart/index.vue"

// 当前模块引入
import Loading from "@p-v/packages/Loading.vue"
import { ref, nextTick, provide, watch } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import { EChartsOption } from "echarts"
import { requestAdapter } from "@p-v/api"
import config from "./config"
import { getWebGlBMap } from "@p-v/utils"
import { cssToJson, jsonToCss } from "@p-v/utils/cssJson"

const chartComponent = ref<InstanceType<typeof Chart>>()

defineOptions({
  name: "PftMapChart",
})

const emits = defineEmits(["click"])
interface ComponentProps {
  style: { [key: string]: string }
  option?: EChartsOption
  styleJson?: string
  showTraffic?: boolean
  mapOpt?: {
    poiText?: boolean
    poiIcon?: boolean
    building?: boolean
    heading?: number
    tilt?: number
    skyColors?: string[]
  }
}

const props = withDefaults(defineProps<ComponentProps>(), {
  style: () => ({}),
  option: () => ({}),
  styleJson: () => "",
  showTraffic: () => false,
  mapOpt: () => ({
    poiText: false,
    poiIcon: false,
    building: false,
  }),
})

const componentLoading = ref<boolean>(true)

const bmap = ref<any>(undefined)

const clearAnchorBL = (dom: HTMLBaseElement, opt: any) => {
  const classList = dom.classList
  if (classList?.contains("anchorBL")) {
    opt.isRemove = true
    dom.remove()
  } else {
    for (let i = 0; i < dom.childNodes.length; i++) {
      clearAnchorBL(dom.childNodes[i] as HTMLBaseElement, opt)
    }
  }
}

const removeAnchorBL = () => {
  const opt = {
    isRemove: false,
  }
  const bmapEl = chartComponent.value?.chartDom as unknown as HTMLBaseElement
  if (bmapEl) {
    clearAnchorBL(bmapEl, opt)
  }
  if (opt.isRemove) {
    return true
  }
  return false
}

const setCenter = (point: { lng: number; lat: number }) => {
  bmap.value?.setCenter(new window.BMapGL.Point(point.lng, point.lat))
}

provide("bmap", bmap)
provide("coordinateSystem", "bmap")

const chartConfig = ref(cloneDeep(defaultsDeep(props.option, config)))

const updateSeries = (data: { id: string }) => {
  const { id } = data
  const series = chartConfig.value.series
  const index = series.findIndex((item: any) => item.id === id)
  if (index > -1) {
    series.splice(index, 1)
    chartConfig.value.series = series
  } else {
    chartConfig.value.series.push(data)
  }
}

provide("updateSeries", updateSeries)

const markInfoBoxActiveComponentId = ref<string>("")

provide("markInfoBoxActiveComponentId", markInfoBoxActiveComponentId)

const loading = () => {
  componentLoading.value = true
}

watch(
  () => chartConfig.value,
  val => {
    // console.log("chartConfig update", val)
  },
  {
    deep: true,
  },
)

watch(
  () => bmap.value,
  val => {
    if (val) {
      bmap.value.on("click", () => {
        markInfoBoxActiveComponentId.value = ""
        emits("click")
      })
    }
  },
  {
    immediate: true,
  },
)

let unWatchBmap: any = null
const setData = async (data: ComponentProps) => {
  await getWebGlBMap("2kaUWT37IuX0PlsU2hZz39XvLqrck9C7")

  const { option, styleJson } = data
  let styleJsonData: { [key: string]: string | number | boolean } = {}
  if (styleJson || props.styleJson) {
    const jsonReq = await requestAdapter<{ [key: string]: string | number | boolean }>({
      url: styleJson || props.styleJson,
      method: "GET",
    })
    if (jsonReq) {
      styleJsonData = jsonReq
    }
  }
  chartConfig.value = defaultsDeep(
    option || {},
    {
      bmap: {
        mapStyleV2: {
          styleJson: styleJsonData,
        },
      },
    },
    props.option,
    config,
    chartConfig.value,
  )
  componentLoading.value = false
  unWatchBmap = watch(
    () => {
      return chartComponent.value?.getModel().getComponent("bmap")?.getBMap()
    },
    (bampInstance: any) => {
      if (!bampInstance) return
      unWatchBmap && unWatchBmap()
      unWatchBmap = null
      bmap.value = bampInstance

      if (props.showTraffic) {
        // 展示交通图层
        bmap.value.setTrafficOn()
      }
      const displayOptions: any = {
        poiText: props.mapOpt?.poiText, // 隐藏poi标注
        poiIcon: props.mapOpt?.poiIcon, // 隐藏poi图标
        building: props.mapOpt?.building, // 隐藏楼块
      }
      if (props.mapOpt?.skyColors) {
        displayOptions.skyColors = props.mapOpt?.skyColors
      }
      bmap.value.setDisplayOptions(displayOptions)
      if (props.mapOpt?.heading) {
        bmap.value.setHeading(props.mapOpt?.heading)
      }
      if (props.mapOpt?.tilt) {
        bmap.value.setTilt(props.mapOpt?.tilt)
      }
      const chartDom = document.querySelector(".ec-extension-bmap")
      if (chartDom) {
        const style = chartDom.getAttribute("style")
        // 结构化style
        const styleObj = cssToJson(style || "")
        // 设置背景色
        styleObj["background-color"] = props.style.backgroundColor || "#fff"
        // 设置样式
        chartDom.setAttribute("style", jsonToCss(styleObj))
      }

      let roundTimes = 0
      const removeTimer = setTimeout(() => {
        const isRemove = removeAnchorBL()
        if (isRemove || roundTimes > 1000) {
          clearTimeout(removeTimer)
        }
        roundTimes++
      }, 100)
    },
    {
      immediate: true,
    },
  )
}

defineExpose({ setData, loading, bmap, setCenter })
</script>

<style lang="scss">
.map-2d-chart {
  height: 100%;
  position: relative;
}
</style>
